import MerakiSDK from "@urbanpiper-engineering/meraki-sdk";
import React, { useEffect, useState } from "react";
import configSelector from "../../external/configSelector";
import dynamic from "next/dynamic";
import { UserState } from "@urbanpiper-engineering/meraki-sdk/dist/esm/user/states/user.state";
import PubSub, { EVENTS } from "../../utils/listeners";
import useConfirm from "@urbanpiper-engineering/meraki-components/dist/Molecules/Confirm";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import ScreenHelper from "../../external/helpers/screenHelper";
import { useIsMobile } from "../../hooks/is_mobile.hook";
import type { ImageType } from "@urbanpiper-engineering/meraki-sdk/dist/esm/graphql/generated";
import checkLightOrDark from "../../external/helpers/checkLightOrDark";
import colorConstants from "../../constants/colors.json";
import { getHeaderColors } from "../../utils/themeColors";

const UserWidget = dynamic(
  () =>
    import(
      "@urbanpiper-engineering/meraki-components/dist/Molecules/UserWidget"
    ),
  {
    ssr: false,
  }
);

const LoginDialog = dynamic(
  () =>
    import(
      "@urbanpiper-engineering/meraki-components/dist/Organisms/LoginDialog"
    ),
  {
    ssr: false,
  }
);

function Login({
  openDialog,
  isdCode,
  currency,
}: {
  openDialog: boolean;
  webBgImg: any;
  isdCode: string;
  currency: string;
}) {
  const isMobile = useIsMobile();
  const useUserState = MerakiSDK.getUserState().hook();
  const user = useUserState((state: typeof UserState) => state.user);
  const router = useRouter();

  const [_open, setOpen] = useState<boolean>(openDialog);
  useEffect(() => setOpen(openDialog), [openDialog]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);
  const [loginBanner, setLoginBanner] = useState();
  const [preLoginData, setPreLoginData] = useState(null);
  const [phoneNo, setPhoneNo] = useState<string>("");
  const [googleSDK, setGoogleSDK] = useState<any>(null);

  const googleLogin = configSelector.getGoogleLogin();
  const facebookLogin = configSelector.getFacebookLogin();
  const guestEnabled = configSelector.getIsGuestCheckoutEnabled();
  const guestEmailRequired = configSelector.getIsEmailRequiredGuestCheckout();
  const termsPageUrl = configSelector.getTermsPageUrl();
  const privacyPageUrl = configSelector.getPrivacyPageUrl();

  const { t } = useTranslation();

  const confirmationDialog = useConfirm();

  useEffect(() => {
    const listener = PubSub.addListener(EVENTS.LOGIN_REQUIRED, () =>
      setOpen(true)
    );
    return () => {
      PubSub.removeListener(listener);
    };
  }, []);

  useEffect(() => {
    if (!loginBanner && _open) {
      (async () => {
        const banners = await MerakiSDK.getBannerImages({
          type: ["web_popup"] as ImageType[],
        });
        if (banners?.images?.length > 0) {
          // pick a random banner
          setLoginBanner(
            banners.images[Math.floor(Math.random() * banners.images.length)]
              .image
          );
        }
      })();
    }
  }, [loginBanner, _open]);

  useEffect(() => {
    (async () => {
      if (_open && googleLogin.enable) {
        const googleSignIn = await MerakiSDK.getGoogleSignIn();
        setGoogleSDK(googleSignIn);
      }
      if (_open && facebookLogin.enable) {
        const facebookSignIn = await MerakiSDK.getFacebookSignIn();
        await facebookSignIn.init(facebookLogin.appID, socialLoginCallback);
      }
    })();
  }, [googleLogin, _open]);

  useEffect(() => {
    if (!_open) {
      setPreLoginData(null);
    }
  }, [_open]);

  const socialLoginCallback = async (resp: any) => {
    if (resp.status === "error") {
      if (resp.message === "phone_number_required") {
        setPreLoginData(resp.data);
      }
      if (resp.message === "userbiz_phone_not_validated") {
        setPreLoginData({ ...resp.data, phone: resp.phone });
        setError(null);
      }
    } else {
      setError(null);
      setOpen(false);
    }
  };

  const onGetOtp = async (phone: number, dialCode: string) => {
    setError(null);
    setLoading(true);
    const response = await MerakiSDK.getOTP({
      isd_code: dialCode,
      phone: String(phone),
    });
    setLoading(false);

    if (response?.status === "error") {
      setError(response?.message);
    } else {
      setError(null);
    }
  };

  const onSubmitOtp = async (phone: string, dialCode: string, otp: string) => {
    setError(null);
    setLoading(true);
    const response = await MerakiSDK.verifyOTP({
      isd_code: dialCode,
      phone,
      otp,
    });

    setLoading(false);
    if (response?.status === "error") {
      setError(response?.message);
    } else if (response?.signup) {
      setError(null);
      setPhoneNo(phone);
      setSignupToken(response?.signup);
    } else {
      setError(null);
      setOpen(false);
      setPhoneNo("");
    }
  };

  const onSignup = async (signupObj: any) => {
    setError(null);
    setLoading(true);
    const [firstName, lastName] = signupObj.fullName.split(" ");
    const response = await MerakiSDK.completeSignup(
      {
        id: signupToken || "",
        firstName,
        lastName: lastName || "",
        email: signupObj.email,
        promo: signupObj.optInForPromo,
      },
      phoneNo
    );

    setLoading(false);

    if (response?.status === "error") {
      setError(response?.message);
    } else {
      setError(null);
      setOpen(false);
      setSignupToken(null);
    }
  };

  const onGuestCheckout = async (guestCheckoutObj: any) => {
    setLoading(true);

    const payload = {
      isd_code: guestCheckoutObj.isd_code,
      customerName: guestCheckoutObj.fullName,
      customerPhone: guestCheckoutObj.phoneNo,
      email: guestCheckoutObj?.email || null,
    };
    const response = await MerakiSDK.guestSignup(payload);
    setLoading(false);
    if (response?.status === "error") {
      setError(response?.message);
    } else {
      setError(null);
      setOpen(false);
    }
  };

  const countryCodes = [
    {
      dial_code: isdCode,
      code: currency,
    },
  ];

  const onSocialLogin = async (provider: string) => {
    if (provider === "facebook") {
      const fbSDK = await MerakiSDK.getFacebookSignIn();
      await fbSDK.triggerLogin();
    }
  };

  const handleTermsPageUrlClick = async () => {
    if (termsPageUrl) {
      await router.push(termsPageUrl);
      setOpen(false);
    } else {
      window.open(
        `https://www.urbanpiper.com/legal/customer-terms-and-conditions`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handlePrivacyPageUrlClick = async () => {
    if (privacyPageUrl) {
      await router.push(privacyPageUrl);
      setOpen(false);
    } else {
      window.open(
        `https://www.urbanpiper.com/legal/privacy-policy`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const handleLogoutClick = async () => {
    confirmationDialog({
      title: t("common.logoutAlertTitle"),
      content: t("common.logoutAlert"),
      removeCloseIcon: false,
      confirmText: t("header.logout"),
      variant: "delete",
      onConfirm: async () => {
        await MerakiSDK.logout();
        router.push("/menu");
      },
    });
  };

  return (
    <div className="login-widget">
      <UserWidget
        isGuestCheckout={user?.is_guest_checkout}
        onProfileClick={() => {
          router.push("/profile");
        }}
        onLoginClick={() => setOpen(true)}
        userName={
          user ? `${[user?.first_name, user?.last_name].join(" ")}` : undefined
        }
        onLogoutClick={handleLogoutClick}
        themeColors={getHeaderColors()}
      />
      <LoginDialog
        loginConfig={{
          facebookEnabled: facebookLogin.enable,
          googleEnabled: googleLogin.enable,
          isGuestCheckoutEnabled:
            guestEnabled && ScreenHelper.isCheckoutPage(router.pathname),
        }}
        preLoginData={preLoginData}
        backgroundImage={loginBanner}
        mobileBackgroundImage={loginBanner}
        googleClientId={googleLogin.clientID}
        onGoogleLoginSuccess={socialLoginCallback}
        googleSDK={googleSDK}
        onGetOtp={onGetOtp}
        onVerifyOTP={onSubmitOtp}
        isOpen={_open}
        onClose={() => setOpen(false)}
        errorMessage={error}
        loading={loading}
        countryCodes={countryCodes}
        signupRequired={signupToken !== null}
        onSignup={onSignup}
        isGuestEmailRequired={guestEmailRequired}
        onGuestCheckout={onGuestCheckout}
        onSocialLogin={onSocialLogin}
        onClickTermsPageUrl={handleTermsPageUrlClick}
        onClickPrivacyPageUrl={handlePrivacyPageUrlClick}
      />
    </div>
  );
}

export default Login;
